import { createSelector } from "@reduxjs/toolkit";
import type { IMcappsPnsNode } from "@multiconsult-group/multiplacer-core";
import type { GetMcappsProjectPnsApiArg } from "../../../openapi";
import { openapi } from "../../../openapi";
import type { IRootState } from "../../app/store";
import { selectProjectNo } from "../designSlice";
import { selectExpandedPnsIds, selectSelectedPnsIds } from "./pnsSelectionSlice";

const EMPTY_PNS_ITEMS: IMcappsPnsNode[] = [];

export const selectGetMcappsPnsArgs = (state: IRootState): GetMcappsProjectPnsApiArg | null => {
  const projectNo = selectProjectNo(state);

  if (!projectNo) {
    return null;
  }

  return { projectNo };
};

export const selectMcappsPnsItems = (state: IRootState): IMcappsPnsNode[] => {
  const args = selectGetMcappsPnsArgs(state);
  if (!args) return EMPTY_PNS_ITEMS;

  return openapi.endpoints.getMcappsProjectPns.select(args)(state).data ?? EMPTY_PNS_ITEMS;
};

export const selectValidSelectedPnsIds = createSelector(
  [selectSelectedPnsIds, selectMcappsPnsItems],
  (selectedIds, items): string[] => {
    const validPnsIdSet = new Set(items.map((item) => item.props.pnsId));
    return selectedIds.filter((id) => validPnsIdSet.has(id));
  }
);

export const selectNextExpandedPnsNodeIds = createSelector(
  [selectExpandedPnsIds, selectMcappsPnsItems],
  (expandedIds, items): string[] => {
    const validNodeIdSet = new Set(items.map((item) => item._id));
    const filteredExpanded = expandedIds.filter((id) => validNodeIdSet.has(id));
    const rootIds = items.filter((item) => item.parentId == null).map((item) => item._id);

    return filteredExpanded.length === 0 && items.length > 0 ? rootIds : filteredExpanded;
  }
);

export const selectSelectedParentPnsIdsFromSelection = createSelector(
  [selectSelectedPnsIds, selectMcappsPnsItems],
  (selectedIds, items): string[] => {
    if (!items.length) return [];

    const selectedSet = new Set(selectedIds);
    const hasChildrenNodeIdSet = new Set(
      items.filter((item) => item.parentId != null).map((item) => item.parentId)
    );
    const seen = new Set<string>();
    const out: string[] = [];

    for (const item of items) {
      const pnsId = item.props.pnsId;
      if (!hasChildrenNodeIdSet.has(item._id) || !selectedSet.has(pnsId) || seen.has(pnsId)) {
        continue;
      }
      seen.add(pnsId);
      out.push(pnsId);
    }

    return out;
  }
);

export const selectSelectedPnsPrefixes = createSelector([selectSelectedPnsIds], (selectedIds) => {
  return Array.from(new Set(selectedIds.map((id) => id.trim()).filter(Boolean)));
});
